import { useEffect, useState, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import api from "../services/api";
import { getApiBaseUrl } from "../config";

export default function useNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connection, setConnection] = useState(null);
  const MAX_ITEMS = 30;

  const load = useCallback(async () => {
    try {
      // Traer últimas notificaciones (leídas y no leídas) para no perder historial reciente
      const { data } = await api.get("/notifications?take=" + MAX_ITEMS);
      setItems(Array.isArray(data) ? data : []);
      setUnread((Array.isArray(data) ? data : []).filter(n => !n.isRead).length);
    } catch (error) {
      console.error("Error cargando notificaciones:", error);
    }
  }, []);

  // Desactivado recuento independiente por 500 en backend; usamos carga completa como en chat

  const markAllRead = async () => {
    try {
      const ids = items.map(x => x.id);
      if (!ids.length) return;
      await api.post("/notifications/read", { ids });
      const updated = items.map(item => ({ ...item, isRead: true }));
      setItems(updated);
      setUnread(0);
      // Limpieza progresiva: borrar en backend los más antiguos leídos si excede MAX_ITEMS
      cleanupOldRead(updated);
    } catch (error) {
      console.error("Error marcando notificaciones como leídas:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.post("/notifications/read", { ids: [id] });
      setItems(prev => {
        const next = prev.map(item => item.id === id ? { ...item, isRead: true } : item);
        setUnread(next.filter(n => !n.isRead).length);
        return next;
      });
    } catch (error) {
      console.error("Error marcando notificación como leída:", error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems(prev => prev.filter(n => n.id !== id));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error eliminando notificación:", error);
    }
  };

  const deleteAll = async () => {
    try {
      await api.delete('/notifications');
      setItems([]);
      setUnread(0);
    } catch (error) {
      console.error('Error eliminando todas las notificaciones:', error);
    }
  };

  // Elimina progresivamente notificaciones leídas más antiguas si excede MAX_ITEMS
  const cleanupOldRead = useCallback(async (current = items) => {
    try {
      const list = [...current];
      if (list.length <= MAX_ITEMS) return;
      const readOldest = list
        .filter(n => n.isRead)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const overflow = list.length - MAX_ITEMS;
      const toDelete = readOldest.slice(0, Math.min(overflow, 10)); // borrar como máximo 10 por ciclo
      for (const n of toDelete) {
        try { await api.delete(`/notifications/${n.id}`); } catch { }
      }
      // Refrescar lista después de limpiar
      load();
    } catch (e) {
      // silencioso
    }
  }, [items, load]);

  // Ref para socket
  const socketRef = useRef(null);

  useEffect(() => {
    load();

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return;

    // Obtener userId del token JWT
    let userId = null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const payload = JSON.parse(jsonPayload);
      userId = payload.sub || payload.id;
    } catch (e) {
      console.error("Error al decodificar token para notificaciones:", e.message);
      return;
    }

    if (!userId) return;

    // Definir handler de recepción
    const handleReceive = (notification) => {
      setItems(prev => {
        // Evitar duplicados 
        if (prev.some(n => n.id === notification.id)) return prev;

        const next = [notification, ...prev].slice(0, MAX_ITEMS);
        setUnread(next.filter(n => !n.isRead).length);
        return next;
      });
    };

    // Reutilizar conexión existente si es válida
    if (window.notificationsSocket && window.notificationsSocket.connected) {
      setConnection(window.notificationsSocket);
      socketRef.current = window.notificationsSocket;

      // Reasignar listener para asegurar closure actualizado (aunque setItems usa callback)
      window.notificationsSocket.off('notification');
      window.notificationsSocket.on('notification', handleReceive);
      return;
    }

    // Limpiar conexión previa desconectada o inválida
    if (window.notificationsSocket) {
      window.notificationsSocket.disconnect();
      window.notificationsSocket = null;
    }

    // Conectar a Socket.IO
    const socketUrl = `${getApiBaseUrl()}/notifications`;
    const newSocket = io(socketUrl, {
      query: { userId },
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    newSocket.on('connect', () => {
      load();
    });

    newSocket.on('connect_error', (err) => {
      // Silenciar errores comunes de polling o red temporal
    });

    newSocket.on('notification', handleReceive);

    setConnection(newSocket);
    socketRef.current = newSocket;
    window.notificationsSocket = newSocket;

    return () => {
      if (socketRef.current) {
        socketRef.current.off('notification', handleReceive);
        // No desconectamos al desmontar para mantener conexión en navegación SPA
      }
    };
  }, [load]);

  // Polling de respaldo (como en chat): recargar lista periódicamente
  useEffect(() => {
    const interval = setInterval(() => {
      load();
    }, 5000);
    return () => clearInterval(interval);
  }, [load]);

  return {
    items,
    unread,
    markAllRead,
    markAsRead,
    deleteNotification,
    deleteAll,
    connection
  };
}
